import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Tag, Button, Typography, Empty, Spin, Input } from 'antd';
import { ShoppingOutlined, StarFilled, SearchOutlined, RightOutlined } from '@ant-design/icons';
import { scenarioSiteAPI } from '../../services/api';
import SiteSectionRenderer from './SiteSectionRenderer';

const { Title, Text, Paragraph } = Typography;
const { Meta } = Card;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darkenColor(hex, amount = 0.2) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

export default function ScenarioSiteHome({ slug, siteInfo, showAllProducts }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const lum = getLuminance(brandColor);
  const darkBrand = lum > 0.55 ? darkenColor(brandColor, 0.3) : brandColor;
  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const btnRadius = isMinimal ? 0 : isClassic ? 4 : 24;

  useEffect(() => {
    (async () => {
      try {
        const [prodRes, secRes] = await Promise.all([
          scenarioSiteAPI.getProducts(slug, i18n.language),
          scenarioSiteAPI.getSections(slug, i18n.language).catch(() => ({ data: [] })),
        ]);
        setProducts(prodRes.data || []);
        const secs = secRes.data || [];
        setSections(secs.length > 0 ? secs : null);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [slug, i18n.language]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  if (sections && !showAllProducts) {
    const hasHeroSection = sections.some(s => s.section_type === 'hero');
    return (
      <div>
        {!hasHeroSection && siteInfo?.hero_title && (
          <SiteSectionRenderer
            section={{ id: 'auto-hero', section_type: 'hero', title: siteInfo.hero_title, config: {} }}
            slug={slug}
            siteInfo={siteInfo}
          />
        )}
        {sections.map(section => (
          <SiteSectionRenderer key={section.id} section={section} slug={slug} siteInfo={siteInfo} />
        ))}
      </div>
    );
  }

  const filtered = search
    ? products.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.short_description || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.organization || '').toLowerCase().includes(search.toLowerCase())
      )
    : products;

  const featured = filtered.filter(p => p.is_featured);
  const regular = filtered.filter(p => !p.is_featured);

  const heroBg = isMinimal
    ? '#ffffff'
    : isClassic
    ? `linear-gradient(180deg, ${darkBrand}, ${darkenColor(darkBrand, 0.12)})`
    : `linear-gradient(135deg, ${darkBrand}, ${darkenColor(darkBrand, 0.2)})`;

  const heroTitleColor = isMinimal ? '#1a1a2e' : '#fff';
  const heroSubColor = isMinimal ? '#555' : 'rgba(255,255,255,0.85)';
  const heroBtnBg = isMinimal ? brandColor : '#fff';
  const heroBtnColor = isMinimal ? '#fff' : darkBrand;

  const renderProductCard = (p) => (
    <Col key={p.id} xs={24} sm={12} md={8} lg={6}>
      <Card
        hoverable
        onClick={() => navigate(`/s/${slug}/products/${p.id}`)}
        style={{
          borderRadius: cardRadius,
          overflow: 'hidden',
          border: isMinimal ? '1px solid #e8e8e8' : undefined,
          boxShadow: isMinimal ? 'none' : isClassic ? '0 1px 4px rgba(0,0,0,0.08)' : undefined,
        }}
        cover={
          p.thumbnail ? (
            <div style={{ overflow: 'hidden', background: '#f5f5f5' }}>
              <img alt={p.name} src={p.thumbnail} style={{ width: '100%', display: 'block' }} />
            </div>
          ) : (
            <div style={{
              height: 180,
              background: `linear-gradient(135deg, ${brandColor}22, ${brandColor}44)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: isMinimal ? 0 : `${cardRadius}px ${cardRadius}px 0 0`,
            }}>
              <ShoppingOutlined style={{ fontSize: 48, color: brandColor }} />
            </div>
          )
        }
        styles={{ body: { padding: '16px' } }}
      >
        <Meta
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {p.is_featured && <StarFilled style={{ color: '#faad14', fontSize: 14 }} />}
              <span style={{ fontSize: 14 }}>{p.name}</span>
            </div>
          }
          description={
            <div>
              {p.organization && <Tag color="blue" style={{ marginBottom: 6 }}>{p.organization}</Tag>}
              <Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>{p.short_description || ''}</Paragraph>
              {p.custom_price_note && <Text strong style={{ color: brandColor, fontSize: 13 }}>{p.custom_price_note}</Text>}
            </div>
          }
        />
      </Card>
    </Col>
  );

  return (
    <div>
      {!showAllProducts && siteInfo?.hero_title && (
        <div style={{
          background: heroBg,
          color: heroTitleColor,
          padding: isMinimal ? '60px 24px' : isClassic ? '70px 24px 60px' : '80px 24px 60px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: isMinimal ? `3px solid ${brandColor}` : 'none',
        }}>
          {siteInfo.hero_image_url && !isMinimal && (
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `url(${siteInfo.hero_image_url})`,
              backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.2,
            }} />
          )}
          <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto' }}>
            <Title level={1} style={{
              color: heroTitleColor,
              marginBottom: 12,
              fontSize: isMinimal ? 36 : isClassic ? 38 : 40,
              lineHeight: 1.2,
              letterSpacing: isMinimal ? 2 : isClassic ? 1 : 0,
              fontWeight: isMinimal ? 300 : 700,
            }}>
              {siteInfo.hero_title}
            </Title>
            {siteInfo.hero_subtitle && (
              <Paragraph style={{
                color: heroSubColor,
                fontSize: isMinimal ? 16 : 18,
                marginBottom: 32,
                lineHeight: 1.6,
              }}>
                {siteInfo.hero_subtitle}
              </Paragraph>
            )}
            <Button
              type="primary"
              size="large"
              onClick={() => navigate(`/s/${slug}/contact`)}
              style={{
                background: heroBtnBg,
                color: heroBtnColor,
                border: isMinimal ? `2px solid ${brandColor}` : 'none',
                borderRadius: btnRadius,
                fontWeight: 600,
                height: 48,
                padding: '0 32px',
                fontSize: 16,
              }}
            >
              {siteInfo.hero_cta_text || t('publicSite.hero.defaultCta')}
            </Button>
          </div>
        </div>
      )}
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <Title level={3} style={{
            margin: 0,
            fontWeight: isMinimal ? 300 : undefined,
            letterSpacing: isMinimal ? 1 : 0,
            borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
            paddingBottom: isMinimal ? 4 : 0,
          }}>
            {showAllProducts ? t('publicSite.home.allProducts') : t('publicSite.home.featuredProducts')}
          </Title>
          <Input prefix={<SearchOutlined />} placeholder={t('publicSite.home.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} style={{ width: 240 }} allowClear />
        </div>
        {filtered.length === 0 ? (
          <Empty description={t('publicSite.home.noProducts')} />
        ) : (
          <>
            {featured.length > 0 && !showAllProducts && (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>{featured.map(renderProductCard)}</Row>
                {regular.length > 0 && <Title level={4} style={{ marginBottom: 16 }}>{t('publicSite.home.moreProducts')}</Title>}
              </>
            )}
            <Row gutter={[16, 16]}>{(showAllProducts ? filtered : regular).map(renderProductCard)}</Row>
          </>
        )}
        {!showAllProducts && products.length > 4 && (
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Button type="link" size="large" onClick={() => navigate(`/s/${slug}/products`)} icon={<RightOutlined />}>{t('publicSite.home.viewAll', { count: products.length })}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
